import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, ShieldCheck, Loader2, CreditCard } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { authApi } from "../../lib/auth";
import { errorMessage } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import MfaCard from "../../components/workspace/MfaCard";
import PasswordCard from "../../components/workspace/PasswordCard";
import EmailAlertsCard from "../../components/workspace/EmailAlertsCard";

export function ChangePasswordPage() {
  const navigate = useNavigate();
  return (
    <div className="ws-page narrow-page" data-testid="change-password-page">
      <PasswordCard forced onChanged={() => navigate("/app/tenants", { replace: true })} />
    </div>
  );
}

export default function SettingsPage() {
  const { org, isAdmin, refresh, user } = useAuth();
  const [name, setName] = useState(org?.name || "");
  const [busy, setBusy] = useState(false);
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    authApi.org.get().then((o) => setCounts(o.counts)).catch(() => {});
  }, []);

  const saveOrg = async (patch) => {
    setBusy(true);
    try {
      await authApi.org.patch(patch);
      await refresh();
      toast.success("Organization updated");
    } catch (err) {
      toast.error("Could not update organization", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ws-page" data-testid="settings-page">
      <header className="ws-head">
        <div>
          <h1>Settings</h1>
          <p>Your organization and account security.</p>
        </div>
      </header>
      {org?.require_mfa && !user.mfa_enabled && (
        <div className="ws-alert warn big" data-testid="mfa-required-banner">
          <ShieldCheck size={16} /> Your organization requires two-step verification. Set up MFA below to access tenants and scans.
        </div>
      )}
      <div className="settings-grid">
        <section className="ws-card glass" data-testid="org-card">
          <h2>
            <Building2 size={16} /> Organization
          </h2>
          {counts && (
            <p className="muted">
              {counts.users} users · {counts.tenants} tenants · {counts.scans} completed scans
            </p>
          )}
          {isAdmin ? (
            <>
              <form
                className="ws-inline-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  saveOrg({ name });
                }}
              >
                <Input value={name} onChange={(e) => setName(e.target.value)} minLength={2} required className="w-72" data-testid="org-name-input" />
                <button type="submit" className="tb-btn sm" disabled={busy || name === org?.name} data-testid="org-name-save">
                  {busy ? <Loader2 size={13} className="spin" /> : null} Rename
                </button>
              </form>
              <label className="ws-switch" data-testid="org-require-mfa">
                <Switch checked={!!org?.require_mfa} onCheckedChange={(v) => saveOrg({ require_mfa: v })} disabled={busy} />
                <span>
                  <b>Require MFA for everyone</b>
                  <small>Members without MFA are asked to enroll at sign-in and cannot turn it off.</small>
                </span>
              </label>
            </>
          ) : (
            <p>
              <b>{org?.name}</b> · you are a standard user. {org?.require_mfa ? "MFA is required." : ""}
            </p>
          )}
        </section>

        <EmailAlertsCard />
        <MfaCard />
        <PasswordCard />

        <section className="ws-card glass muted-card" data-testid="billing-card">
          <h2>
            <CreditCard size={16} /> Billing
          </h2>
          <p className="muted">Plans and invoices will appear here. {isAdmin ? "Admins will manage billing." : ""}</p>
          <span className="mini-chip">coming soon</span>
        </section>

        <section className="ws-card glass muted-card">
          <h2>
            <ShieldCheck size={16} /> Account
          </h2>
          <dl className="detail-grid">
            <div>
              <dt>Name</dt>
              <dd>{user.name}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{user.email}</dd>
            </div>
            <div>
              <dt>Role</dt>
              <dd>{user.role}</dd>
            </div>
            <div>
              <dt>Member since</dt>
              <dd>{new Date(user.created_at).toLocaleDateString()}</dd>
            </div>
          </dl>
        </section>
      </div>
    </div>
  );
}
